import { useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import commonStyles from '../common.module.css';
import Input from '../../common/Input/Input';
import Customer from '../../../models/Customer';
import Select from '../../common/Select/Select';
import styles from './RepresentativeView.module.css';
import { createNewCustomer } from '../../../services/customer/customerService';
import APIError from '../../../services/APIError';
import { saveRepresentative } from '../../../services/representative/representativeService';
import Error from '../../common/Error/Error';
import warningLogo from '../../../assets/images/warning.png';
import { isValidCustomer, isValidEmail, isValidPhone } from '../../../utils/validation';
import CustomerContext from '../../../context/customer-context';
import CountrySelect from '../../common/CountrySelect/CountrySelect';

const initCustomer: Customer = {
  personType: 'PRIVATE',
  firstName: '',
  name: '',
  country: '',
  email: '',
  registrationCode: '',
  phone: '',
  preferredLanguage: '',
  servicesProvided: [],
  questionnaireTypesRequired: [],
};

type RepresentativeRegisterProps = {
  onCancel: () => void,
  customer: Customer
}

function RepresentativeRegister(props: RepresentativeRegisterProps) {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const [newCustomer, setNewCustomer] = useState<Customer>(initCustomer);
  const [relationDescription, setRelationDescription] = useState<string>();
  const [error, setError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const customerCtx = useContext(CustomerContext);

  const registerRepresentative = async () => {
    try {
      return await createNewCustomer({ ...newCustomer, customerStatus: 'NON_CLIENT' });
    } catch (err) {
      if (err instanceof APIError) {
        console.log(`Failed to save customer: ${err.errorBody}`);
        setError(true);
        setErrorText(t(`customer.validation.${err.errorBody}`));
      } else {
        console.log(err);
        setError(true);
        setErrorText(t(`customer.validation.creationFailed`));
      }
    }
  };

  const registerRelation = async () => {
    try {
      const representative = await registerRepresentative();
      await saveRepresentative({ customer: props.customer, representative, relationDescription });
      customerCtx.setSuccessMessage(t('representative.representativeAdded'));
      navigation('/customer');
    } catch (err) {
      if (err instanceof APIError) {
        console.log(`Failed to save representative: ${err.errorBody}`);
      } else {
        console.log(err);
      }
    }
  };

  const handleCreate = async () => {
    if (isValidCustomer(newCustomer) && relationDescription) {
      registerRelation();
    } else {
      setError(true);
      setErrorText(t('customer.validation.warningMessage'));
    }
  };

  return (
    <>
      <div className={styles.errorContainer}>
        {errorText &&
          <Error
            messageText={errorText}
            logoSrc={warningLogo}
            logoAltText={t('customer.validation.warningLogo')}
          />
        }
      </div>
      <div>
        <div className={commonStyles.row}>
          <div className={styles.field}>
            <Input
              id='firstName'
              label={t('customer.basicInfo.label.firstName')}
              placeholder={t('customer.basicInfo.placeholder.firstName')}
              required
              maxLength={70}
              type='text'
              onUpdate={(value) => setNewCustomer({
                ...newCustomer,
                firstName: value,
              })}
              error={error && newCustomer.firstName === ''}
            />
          </div>
          <div className={styles.field}>
            <Input
              id='lastName'
              label={t('customer.basicInfo.label.lastName')}
              placeholder={t('customer.basicInfo.placeholder.lastName')}
              required
              maxLength={70}
              type='text'
              onUpdate={(value) => setNewCustomer({
                ...newCustomer,
                name: value,
              })}
              error={error && newCustomer.name === ''}
            />
          </div>
          <div className={styles.field}>
            <Input
              id='personalId'
              label={t('customer.basicInfo.label.personalId')}
              placeholder={t('customer.basicInfo.placeholder.personalId')}
              required
              maxLength={35}
              type='text'
              onUpdate={(value) => setNewCustomer({
                ...newCustomer,
                registrationCode: value,
              })}
              error={error && newCustomer.registrationCode === ''}
            />
          </div>
        </div>
        <div className={commonStyles.row}>
          <div className={styles.field}>
            <CountrySelect
              required
              label={t('customer.basicInfo.label.citizenship')}
              onUpdate={(value) => setNewCustomer({
                ...newCustomer,
                country: value,
              })}
              error={error && newCustomer.country === ''}
            />
          </div>
          <div className={styles.field}>
            <Input
              id='phone'
              placeholder={t('customer.basicInfo.placeholder.phone')}
              type='text'
              maxLength={15}
              label={t('customer.basicInfo.label.phone')}
              onUpdate={(value) => setNewCustomer({
                ...newCustomer,
                phone: value,
              })}
              error={error && !isValidPhone(newCustomer.phone, true)}
            />
          </div>
          <div className={styles.field}>
            <Input
              id='email'
              placeholder={t('customer.basicInfo.placeholder.email')}
              type='email'
              label={t('customer.basicInfo.label.email')}
              required
              maxLength={320}
              onUpdate={(value) => setNewCustomer({
                ...newCustomer,
                email: value,
              })}
              error={error && !isValidEmail(newCustomer.email, false)}
            />
          </div>
        </div>
        <div className={commonStyles.row}>
          <div className={styles.field}>
            <Select
              id='language'
              options={[{ value: 'EN' }, { value: 'EE' }, { value: 'LV' }, { value: 'LT' }]}
              label={t('customer.basicInfo.label.language')}
              required
              onUpdate={(value) => setNewCustomer({
                ...newCustomer,
                preferredLanguage: value,
              })}
              error={error && newCustomer.preferredLanguage === ''}
            />
          </div>
          <div className={styles.field} style={{ flexGrow: 2 }}>
            <Input
              id='role'
              label={t('representative.view.label.role')}
              placeholder={t('representative.view.placeholder.role')}
              required
              maxLength={100}
              type='text'
              onUpdate={(value) => setRelationDescription(value)}
              error={error && !relationDescription}
            />
          </div>
        </div>
      </div>
      <div className={styles.buttonsContainer}>
        <div
          role='button'
          tabIndex={0}
          onKeyDown={handleCreate}
          onClick={handleCreate}
          className={styles.button}
        >
          {t('common.create')}
        </div>
        <div
          role='button'
          tabIndex={0}
          onKeyDown={props.onCancel}
          onClick={props.onCancel}
          className={styles.button}
        >
          {t('common.cancel')}
        </div>
      </div>
    </>
  );
}

export default RepresentativeRegister;
